import React, { useState, useEffect, useCallback } from 'react';
import { timePunchAPI, timeOffAPI, leaveBalanceAPI, payrollSettingsAPI, payPeriodAPI } from '../services/api';
import type { TimePunch, TimeOffEntry, LeaveBalance } from '../types';

interface UserOption {
  id: number;
  username: string;
  full_name: string;
}

type EntryType = 'all' | 'punches' | 'timeoff';

interface UnifiedRow {
  kind: 'punch' | 'timeoff';
  sortDate: string;
  punch?: TimePunch;
  timeOff?: TimeOffEntry;
}

const TIME_OFF_LABELS: Record<string, string> = {
  sick: 'Sick Leave',
  vacation: 'Vacation',
  holiday: 'Holiday',
};

function toLocalDate(isoStr: string): string {
  const d = new Date(isoStr);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 10);
}

function toLocalTime(isoStr: string): string {
  const d = new Date(isoStr);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(11, 16);
}

function fromDateAndTime(dateStr: string, timeStr: string): string {
  return new Date(`${dateStr}T${timeStr}`).toISOString();
}

const AdminPunchDashboard: React.FC = () => {
  const [punches, setPunches] = useState<TimePunch[]>([]);
  const [timeOffEntries, setTimeOffEntries] = useState<TimeOffEntry[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [filterUser, setFilterUser] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [filterFlagged, setFilterFlagged] = useState('');
  const [filterEntryType, setFilterEntryType] = useState<EntryType>('all');

  // resolutionNote is used within the edit form for flagged punches
  const [resolutionNote, setResolutionNote] = useState('');

  const [editPunchId, setEditPunchId] = useState<number | null>(null);
  const [editClockInDate, setEditClockInDate] = useState('');
  const [editClockInTime, setEditClockInTime] = useState('');
  const [editClockOutDate, setEditClockOutDate] = useState('');
  const [editClockOutTime, setEditClockOutTime] = useState('');
  const [editAdminNotes, setEditAdminNotes] = useState('');

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createUser, setCreateUser] = useState('');
  const [createClockInDate, setCreateClockInDate] = useState('');
  const [createClockInTime, setCreateClockInTime] = useState('');
  const [createClockOutDate, setCreateClockOutDate] = useState('');
  const [createClockOutTime, setCreateClockOutTime] = useState('');
  const [createAdminNotes, setCreateAdminNotes] = useState('');
  const [lockingEnabled, setLockingEnabled] = useState(true);

  // Time off form state
  const [showTimeOffForm, setShowTimeOffForm] = useState(false);
  const [toEditId, setToEditId] = useState<number | null>(null);
  const [toFormUser, setToFormUser] = useState('');
  const [toFormDate, setToFormDate] = useState('');
  const [toFormHours, setToFormHours] = useState('');
  const [toFormType, setToFormType] = useState('');
  const [toFormNotes, setToFormNotes] = useState('');
  const [overrideBalance, setOverrideBalance] = useState(false);
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalance[]>([]);

  // Pagination state
  const PAGE_SIZE = 50;
  const [currentPage, setCurrentPage] = useState(1);
  const [punchCount, setPunchCount] = useState(0);
  const [timeOffCount, setTimeOffCount] = useState(0);
  const [hasNextPunch, setHasNextPunch] = useState(false);
  const [hasNextTimeOff, setHasNextTimeOff] = useState(false);
  const [hasPrevPunch, setHasPrevPunch] = useState(false);
  const [hasPrevTimeOff, setHasPrevTimeOff] = useState(false);
  const [datesInitialized, setDatesInitialized] = useState(false);

  useEffect(() => {
    payrollSettingsAPI.get()
      .then(res => setLockingEnabled(res.data.pay_period_locking_enabled !== false))
      .catch(() => {});
  }, []);

  const fetchPunches = useCallback(async (page: number = currentPage) => {
    try {
      const params: Record<string, string | number> = { page, page_size: PAGE_SIZE };
      if (filterUser) params.user_id = filterUser;
      if (filterStart) params.start_date = filterStart;
      if (filterEnd) params.end_date = filterEnd;
      if (filterFlagged) params.flagged = filterFlagged;
      const response = await timePunchAPI.getAll(params as any);
      setPunches(response.data.results);
      setPunchCount(response.data.count);
      setHasNextPunch(!!response.data.next);
      setHasPrevPunch(!!response.data.previous);
    } catch (err) {
      console.error('Failed to fetch punches:', err);
    }
  }, [currentPage, filterUser, filterStart, filterEnd, filterFlagged, PAGE_SIZE]);

  const fetchTimeOff = useCallback(async (page: number = currentPage) => {
    try {
      const params: Record<string, string | number> = { page, page_size: PAGE_SIZE };
      if (filterUser) params.user_id = filterUser;
      if (filterStart) params.start_date = filterStart;
      if (filterEnd) params.end_date = filterEnd;
      const response = await timeOffAPI.getAll(params as any);
      setTimeOffEntries(response.data.results);
      setTimeOffCount(response.data.count);
      setHasNextTimeOff(!!response.data.next);
      setHasPrevTimeOff(!!response.data.previous);
    } catch (err) {
      console.error('Failed to fetch time off:', err);
    }
  }, [currentPage, filterUser, filterStart, filterEnd, PAGE_SIZE]);

  const fetchAll = useCallback(async (page: number = currentPage) => {
    await Promise.all([fetchPunches(page), fetchTimeOff(page)]);
  }, [fetchPunches, fetchTimeOff, currentPage]);

  useEffect(() => {
    const loadInitial = async () => {
      setLoading(true);
      try {
        const usersResp = await timePunchAPI.getEmployees();
        setUsers(usersResp.data);
      } catch (err) {
        console.error('Failed to load users:', err);
      }
      try {
        const periodResp = await payPeriodAPI.getCurrent();
        if (periodResp.data) {
          setFilterStart(periodResp.data.start_date);
          setFilterEnd(periodResp.data.end_date);
        }
      } catch {
        // No current period -- leave dates empty
      }
      setDatesInitialized(true);
    };
    loadInitial();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!datesInitialized) return;
    setCurrentPage(1);
    fetchAll(1).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUser, filterStart, filterEnd, filterFlagged, datesInitialized]);

  const fetchLeaveBalances = useCallback(async (userId: string) => {
    if (!userId) { setLeaveBalances([]); return; }
    try {
      const currentYear = new Date().getFullYear();
      const resp = await leaveBalanceAPI.getAll({ user_id: Number(userId), year: currentYear });
      setLeaveBalances(resp.data.results || []);
    } catch { setLeaveBalances([]); }
  }, []);

  useEffect(() => {
    if (showTimeOffForm && toFormUser) {
      fetchLeaveBalances(toFormUser);
    }
  }, [showTimeOffForm, toFormUser, fetchLeaveBalances]);

  // --- Punch handlers ---
  const handleDeletePunch = async (punchId: number) => {
    if (!window.confirm('Are you sure you want to delete this punch?')) return;
    setError('');
    try {
      await timePunchAPI.delete(punchId);
      setSuccess('Punch deleted.');
      await fetchPunches();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete punch.');
    }
  };

  const openEditForm = (punch: TimePunch) => {
    setEditPunchId(punch.id!);
    setEditClockInDate(toLocalDate(punch.clock_in));
    setEditClockInTime(toLocalTime(punch.clock_in));
    if (punch.clock_out) {
      setEditClockOutDate(toLocalDate(punch.clock_out));
      setEditClockOutTime(toLocalTime(punch.clock_out));
    } else {
      setEditClockOutDate('');
      setEditClockOutTime('');
    }
    setEditAdminNotes(punch.admin_notes || '');
    setResolutionNote('');
    closeTimeOffForm();
  };

  const handleSaveEdit = async (andResolve: boolean = false) => {
    if (!editPunchId || !editClockInDate || !editClockInTime) return;
    setError(''); setSuccess('');
    try {
      const data: Record<string, string | null> = { clock_in: fromDateAndTime(editClockInDate, editClockInTime) };
      data.clock_out = (editClockOutDate && editClockOutTime) ? fromDateAndTime(editClockOutDate, editClockOutTime) : null;
      if (editAdminNotes) data.admin_notes = editAdminNotes;
      await timePunchAPI.update(editPunchId, data as any);
      if (andResolve) {
        await timePunchAPI.resolveFlag(editPunchId, resolutionNote);
      }
      setEditPunchId(null);
      setResolutionNote('');
      setSuccess(andResolve ? 'Punch updated and flag resolved.' : 'Punch updated successfully.');
      await fetchPunches();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to update punch.');
    }
  };

  const handleCreatePunch = async () => {
    if (!createUser || !createClockInDate || !createClockInTime) {
      setError('Employee, Clock In date, and Clock In time are required.'); return;
    }
    setError(''); setSuccess('');
    try {
      const data: Record<string, string | number | null> = {
        user: parseInt(createUser, 10),
        clock_in: fromDateAndTime(createClockInDate, createClockInTime),
      };
      if (createClockOutDate && createClockOutTime) data.clock_out = fromDateAndTime(createClockOutDate, createClockOutTime);
      if (createAdminNotes) data.admin_notes = createAdminNotes;
      await timePunchAPI.create(data as any);
      setShowCreateForm(false);
      setCreateUser(''); setCreateClockInDate(''); setCreateClockInTime(''); setCreateClockOutDate(''); setCreateClockOutTime(''); setCreateAdminNotes('');
      setSuccess('Manual punch created successfully.');
      await fetchPunches();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to create punch.');
    }
  };

  // --- Time off handlers ---
  const closeTimeOffForm = () => {
    setShowTimeOffForm(false); setToEditId(null);
    setToFormUser(''); setToFormDate(''); setToFormHours('');
    setToFormType(''); setToFormNotes(''); setOverrideBalance(false);
    setLeaveBalances([]);
  };

  const openTimeOffEdit = (entry: TimeOffEntry) => {
    setToEditId(entry.id!);
    setToFormUser(String(entry.user));
    setToFormDate(entry.date);
    setToFormHours(String(entry.hours));
    setToFormType(entry.time_off_type);
    setToFormNotes(entry.notes || '');
    setOverrideBalance(false);
    setShowTimeOffForm(true);
    setEditPunchId(null); setShowCreateForm(false);
  };

  const handleTimeOffSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!toFormUser || !toFormDate || !toFormHours) {
      setError('Employee, date, and hours are required.'); return;
    }
    const data: any = {
      user: Number(toFormUser), date: toFormDate,
      hours: Number(toFormHours), time_off_type: toFormType, notes: toFormNotes,
    };
    if (overrideBalance) data.override_balance = true;
    try {
      if (toEditId) {
        await timeOffAPI.update(toEditId, data);
        setSuccess('Time off entry updated.');
      } else {
        await timeOffAPI.create(data);
        setSuccess('Time off entry created.');
      }
      closeTimeOffForm();
      await fetchTimeOff();
    } catch (err: any) {
      const detail = err.response?.data;
      let msg: string;
      if (Array.isArray(detail)) msg = detail.join(' ');
      else if (typeof detail === 'string') msg = detail;
      else msg = detail?.detail || detail?.error || JSON.stringify(detail);
      setError(msg || 'Failed to save time off entry.');
    }
  };

  const handleDeleteTimeOff = async (id: number) => {
    if (!window.confirm('Delete this time off entry?')) return;
    try {
      await timeOffAPI.delete(id);
      setSuccess('Entry deleted.');
      await fetchTimeOff();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete.');
    }
  };

  // --- Unified rows ---
  const buildRows = (): UnifiedRow[] => {
    const rows: UnifiedRow[] = [];
    if (filterEntryType !== 'timeoff') {
      for (const p of punches) {
        rows.push({ kind: 'punch', sortDate: p.clock_in, punch: p });
      }
    }
    if (filterEntryType !== 'punches') {
      for (const t of timeOffEntries) {
        rows.push({ kind: 'timeoff', sortDate: t.date + 'T00:00:00', timeOff: t });
      }
    }
    rows.sort((a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime());
    return rows;
  };

  if (loading) {
    return <div className="container"><div className="loading">Loading...</div></div>;
  }

  const unresolvedFlags = punches.filter(p => p.is_flagged && !p.flag_resolved);
  const rows = buildRows();

  const selectedBalance = (toFormType === 'sick' || toFormType === 'vacation')
    ? leaveBalances.find(b => b.leave_type === toFormType) : undefined;
  const balanceWarning = selectedBalance && toFormHours
    ? Number(toFormHours) > Number(selectedBalance.available_hours) : false;

  return (
    <div className="container">
      {unresolvedFlags.length > 0 && (
        <div className="card" style={{ borderLeft: '4px solid #f39c12' }}>
          <h3 style={{ color: '#f39c12', marginBottom: '0.5rem' }}>
            {unresolvedFlags.length} Unresolved Flag{unresolvedFlags.length !== 1 ? 's' : ''}
          </h3>
          <p style={{ color: '#7f8c8d', marginBottom: '1rem' }}>
            Employees have flagged the following punches for your review.
          </p>
          {unresolvedFlags.map(p => (
            <div key={p.id} style={{
              padding: '0.75rem', marginBottom: '0.5rem', backgroundColor: '#fff8e1',
              borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem',
            }}>
              <div>
                <strong>{p.user_full_name || p.user_username}</strong>
                {' — '}{formatDate(p.clock_in)} ({formatTime(p.clock_in)} – {p.clock_out ? formatTime(p.clock_out) : 'Open'})
                <div style={{ color: '#555', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                  "{p.flag_note}"
                </div>
              </div>
              <button className="btn btn-small btn-primary"
                onClick={() => openEditForm(p)}>
                Edit &amp; Resolve
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>Time &amp; Attendance</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-success"
              onClick={() => { closeTimeOffForm(); setShowTimeOffForm(true); setShowCreateForm(false); setEditPunchId(null); }}>
              + Time Off
            </button>
            <button className="btn btn-success"
              onClick={() => { setShowCreateForm(true); setEditPunchId(null); closeTimeOffForm(); }}>
              + Manual Punch
            </button>
          </div>
        </div>

        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        {showCreateForm && (
          <div style={{
            padding: '1.5rem', backgroundColor: '#ffffff', borderRadius: '8px',
            border: '1px solid #ddd', marginBottom: '1.5rem',
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Create Manual Punch</h3>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0, flex: '1 1 180px' }}>
                <label>Employee *</label>
                <select value={createUser} onChange={(e) => setCreateUser(e.target.value)}>
                  <option value="">Select employee...</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0, flex: '1 1 140px' }}>
                <label>Clock In Date *</label>
                <input type="date" value={createClockInDate} onChange={(e) => setCreateClockInDate(e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0, flex: '0 1 120px' }}>
                <label>Clock In Time *</label>
                <input type="time" value={createClockInTime} onChange={(e) => setCreateClockInTime(e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0, flex: '1 1 140px' }}>
                <label>Clock Out Date</label>
                <input type="date" value={createClockOutDate} onChange={(e) => setCreateClockOutDate(e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0, flex: '0 1 120px' }}>
                <label>Clock Out Time</label>
                <input type="time" value={createClockOutTime} onChange={(e) => setCreateClockOutTime(e.target.value)} />
              </div>
            </div>
            <div className="form-group" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
              <label>Admin Notes</label>
              <textarea placeholder="Reason for manual entry..." value={createAdminNotes}
                onChange={(e) => setCreateAdminNotes(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', minHeight: '50px', fontSize: '0.9rem' }} />
            </div>
            <div style={{ marginTop: '1rem' }}>
              <button className="btn btn-success" onClick={handleCreatePunch}>Create</button>
              <button className="btn btn-secondary" style={{ marginLeft: '0.5rem' }} onClick={() => setShowCreateForm(false)}>Cancel</button>
            </div>
          </div>
        )}

        {showTimeOffForm && (
          <div style={{
            padding: '1.5rem', backgroundColor: '#f0f8ff', borderRadius: '8px',
            marginBottom: '1.5rem', border: '1px solid #b8daff',
          }}>
            <h3 style={{ marginBottom: '1rem' }}>{toEditId ? 'Edit' : 'Create'} Time Off Entry</h3>

            {toFormUser && leaveBalances.length > 0 && (
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                {leaveBalances.map(b => (
                  <div key={b.id} style={{
                    padding: '0.5rem 1rem', borderRadius: '6px',
                    backgroundColor: b.leave_type === 'sick' ? '#fef0f0' : '#eff6ff',
                    border: `1px solid ${b.leave_type === 'sick' ? '#f5c6cb' : '#bee3f8'}`,
                    fontSize: '0.85rem',
                  }}>
                    <strong>{b.leave_type === 'sick' ? 'Sick' : 'Vacation'}:</strong>{' '}
                    {b.available_hours}h available
                    <span style={{ color: '#7f8c8d', marginLeft: '0.5rem' }}>
                      ({b.used_hours}h used of {(Number(b.carryover_hours) + Number(b.granted_hours)).toFixed(2)}h)
                    </span>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={handleTimeOffSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Employee *</label>
                  <select value={toFormUser} onChange={(e) => setToFormUser(e.target.value)} required>
                    <option value="">Select...</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Date *</label>
                  <input type="date" value={toFormDate} onChange={(e) => setToFormDate(e.target.value)} required />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Hours *</label>
                  <input type="number" step="0.25" min="0.25" max="24" placeholder=""
                    value={toFormHours} onChange={(e) => setToFormHours(e.target.value)} required />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Type *</label>
                  <select value={toFormType} onChange={(e) => setToFormType(e.target.value)} required>
                    <option value="">Select...</option>
                    <option value="sick">Sick Leave</option>
                    <option value="vacation">Vacation</option>
                    <option value="holiday">Holiday</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '1rem', marginBottom: 0 }}>
                <label>Notes (optional)</label>
                <input type="text" value={toFormNotes} onChange={(e) => setToFormNotes(e.target.value)}
                  placeholder="e.g. Memorial Day, doctor appointment..." />
              </div>

              {balanceWarning && (
                <div style={{
                  marginTop: '1rem', padding: '0.75rem 1rem', backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107', borderRadius: '6px', color: '#856404', fontSize: '0.9rem',
                }}>
                  This entry exceeds the employee's available {toFormType} leave balance
                  ({selectedBalance?.available_hours}h available). Check "Override" below to proceed anyway.
                </div>
              )}

              {(toFormType === 'sick' || toFormType === 'vacation') && (
                <div style={{ marginTop: '0.75rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                    <input type="checkbox" checked={overrideBalance}
                      onChange={(e) => setOverrideBalance(e.target.checked)} style={{ width: 'auto' }} />
                    Override balance check
                  </label>
                </div>
              )}

              <div className="button-group">
                <button type="submit" className="btn btn-success">{toEditId ? 'Update' : 'Create'}</button>
                <button type="button" className="btn btn-secondary" onClick={closeTimeOffForm}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          <div className="form-group" style={{ marginBottom: 0, flex: '1 1 150px' }}>
            <label>Employee</label>
            <select value={filterUser} onChange={(e) => setFilterUser(e.target.value)}>
              <option value="">All</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: '1 1 130px' }}>
            <label>Entry Type</label>
            <select value={filterEntryType} onChange={(e) => setFilterEntryType(e.target.value as EntryType)}>
              <option value="all">All</option>
              <option value="punches">Punches</option>
              <option value="timeoff">Time Off</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: '1 1 150px' }}>
            <label>From</label>
            <input type="date" value={filterStart} onChange={(e) => setFilterStart(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: '1 1 150px' }}>
            <label>To</label>
            <input type="date" value={filterEnd} onChange={(e) => setFilterEnd(e.target.value)} />
          </div>
          {filterEntryType !== 'timeoff' && (
            <div className="form-group" style={{ marginBottom: 0, flex: '1 1 150px' }}>
              <label>Flags</label>
              <select value={filterFlagged} onChange={(e) => setFilterFlagged(e.target.value)}>
                <option value="">All Punches</option>
                <option value="unresolved">Unresolved Flags</option>
                <option value="all">All Flagged</option>
              </select>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
            <button className="btn btn-primary" onClick={() => { setCurrentPage(1); fetchAll(1); }}>Filter</button>
            {(filterStart || filterEnd) && (
              <button className="btn btn-secondary" onClick={() => { setFilterStart(''); setFilterEnd(''); }}>
                Clear Dates
              </button>
            )}
          </div>
        </div>

        <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#7f8c8d' }}>
          Showing {rows.length} entries{(punchCount + timeOffCount) > 0 && ` of ${punchCount + timeOffCount} total`}
          {(filterStart || filterEnd) && (
            <span> &middot; {filterStart && `From ${filterStart}`}{filterStart && filterEnd && ' '}{filterEnd && `To ${filterEnd}`}</span>
          )}
        </div>

        {rows.length === 0 ? (
          <p style={{ color: '#95a5a6' }}>No entries match the current filter.</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Date</th>
                  <th>Clock In</th>
                  <th>Clock Out / Type</th>
                  <th>Hours</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  if (row.kind === 'punch' && row.punch) {
                    const punch = row.punch;
                    return (
                      <React.Fragment key={`p-${punch.id}`}>
                        <tr style={punch.has_unresolved_flag ? { backgroundColor: '#fff8e1' } : undefined}>
                          <td>{punch.user_full_name || punch.user_username}</td>
                          <td>{formatDate(punch.clock_in)}</td>
                          <td>{formatTime(punch.clock_in)}</td>
                          <td>{punch.clock_out ? formatTime(punch.clock_out) : <em style={{ color: '#27ae60' }}>Active</em>}</td>
                          <td>{punch.calculated_hours ?? '—'}</td>
                          <td style={{ textTransform: 'capitalize' }}>{punch.source}</td>
                          <td>
                            {renderStatus(punch)}
                            {lockingEnabled && punch.pay_period_is_locked && (
                              <span className="status-badge" style={{ backgroundColor: '#95a5a6', color: 'white', marginLeft: '0.25rem', fontSize: '0.7rem' }}>Locked</span>
                            )}
                          </td>
                          <td className="actions">
                            <button className="btn btn-small btn-secondary" onClick={() => openEditForm(punch)}
                              disabled={lockingEnabled && punch.pay_period_is_locked}
                              title={lockingEnabled && punch.pay_period_is_locked ? 'Unlock the pay period to edit' : ''}>
                              {punch.has_unresolved_flag ? 'Edit & Resolve' : 'Edit'}
                            </button>
                            <button className="btn btn-small btn-danger" onClick={() => handleDeletePunch(punch.id!)}
                              disabled={lockingEnabled && punch.pay_period_is_locked}
                              title={lockingEnabled && punch.pay_period_is_locked ? 'Unlock the pay period to delete' : ''}>
                              Delete
                            </button>
                          </td>
                        </tr>
                        {editPunchId === punch.id && (
                          <tr>
                            <td colSpan={8} style={{ backgroundColor: '#f0f7ff', padding: '1rem' }}>
                              {punch.has_unresolved_flag && (
                                <div style={{
                                  padding: '0.75rem', marginBottom: '1rem', backgroundColor: '#fff8e1',
                                  borderRadius: '6px', border: '1px solid #f0e6c0',
                                }}>
                                  <strong style={{ color: '#f39c12' }}>Employee flagged this entry:</strong>
                                  <div style={{ marginTop: '0.25rem', color: '#555' }}>"{punch.flag_note}"</div>
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                <div className="form-group" style={{ marginBottom: 0, flex: '1 1 140px' }}>
                                  <label>Clock In Date</label>
                                  <input type="date" value={editClockInDate} onChange={(e) => setEditClockInDate(e.target.value)} />
                                </div>
                                <div className="form-group" style={{ marginBottom: 0, flex: '0 1 120px' }}>
                                  <label>Clock In Time</label>
                                  <input type="time" value={editClockInTime} onChange={(e) => setEditClockInTime(e.target.value)} />
                                </div>
                                <div className="form-group" style={{ marginBottom: 0, flex: '1 1 140px' }}>
                                  <label>Clock Out Date</label>
                                  <input type="date" value={editClockOutDate} onChange={(e) => setEditClockOutDate(e.target.value)} />
                                </div>
                                <div className="form-group" style={{ marginBottom: 0, flex: '0 1 120px' }}>
                                  <label>Clock Out Time</label>
                                  <input type="time" value={editClockOutTime} onChange={(e) => setEditClockOutTime(e.target.value)} />
                                </div>
                                <div className="form-group" style={{ marginBottom: 0, flex: '2 1 250px' }}>
                                  <label>Admin Notes</label>
                                  <input type="text" placeholder="Reason for edit..." value={editAdminNotes} onChange={(e) => setEditAdminNotes(e.target.value)} />
                                </div>
                              </div>
                              {punch.has_unresolved_flag && (
                                <div className="form-group" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                                  <label>Resolution Note</label>
                                  <input type="text" placeholder="e.g. Adjusted clock-in per employee request..."
                                    value={resolutionNote} onChange={(e) => setResolutionNote(e.target.value)} />
                                </div>
                              )}
                              <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                                {punch.has_unresolved_flag ? (
                                  <>
                                    <button className="btn btn-small btn-success" onClick={() => handleSaveEdit(true)}>Save &amp; Resolve</button>
                                    <button className="btn btn-small btn-secondary" onClick={() => handleSaveEdit(false)}>Save Without Resolving</button>
                                  </>
                                ) : (
                                  <button className="btn btn-small btn-success" onClick={() => handleSaveEdit(false)}>Save Changes</button>
                                )}
                                <button className="btn btn-small btn-secondary" onClick={() => setEditPunchId(null)}>Cancel</button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  }

                  if (row.kind === 'timeoff' && row.timeOff) {
                    const entry = row.timeOff;
                    return (
                      <tr key={`to-${entry.id}`} style={{ backgroundColor: '#f0f8ff' }}>
                        <td>{entry.user_full_name || entry.user_username}</td>
                        <td>{new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                        <td colSpan={1} style={{ color: '#7f8c8d', fontStyle: 'italic' }}>—</td>
                        <td>
                          <span className="status-badge" style={typeStyle(entry.time_off_type)}>
                            {TIME_OFF_LABELS[entry.time_off_type] || entry.time_off_type}
                          </span>
                        </td>
                        <td>{entry.hours}</td>
                        <td>{entry.created_by_username || '—'}</td>
                        <td>
                          <span className="status-badge" style={{ backgroundColor: '#8e44ad', color: 'white' }}>Time Off</span>
                          {lockingEnabled && entry.pay_period_is_locked && (
                            <span className="status-badge" style={{ backgroundColor: '#95a5a6', color: 'white', marginLeft: '0.25rem', fontSize: '0.7rem' }}>Locked</span>
                          )}
                        </td>
                        <td className="actions">
                          <button className="btn btn-small btn-primary" onClick={() => openTimeOffEdit(entry)}
                            disabled={lockingEnabled && entry.pay_period_is_locked}
                            title={lockingEnabled && entry.pay_period_is_locked ? 'Unlock the pay period to edit' : ''}>
                            Edit
                          </button>
                          <button className="btn btn-small btn-danger" onClick={() => handleDeleteTimeOff(entry.id!)}
                            disabled={lockingEnabled && entry.pay_period_is_locked}
                            title={lockingEnabled && entry.pay_period_is_locked ? 'Unlock the pay period to delete' : ''}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  }

                  return null;
                })}
              </tbody>
            </table>
          </div>
        )}

        {(hasNextPunch || hasNextTimeOff || hasPrevPunch || hasPrevTimeOff) && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: '1rem', padding: '0.75rem 0',
          }}>
            <button
              className="btn btn-secondary"
              disabled={currentPage <= 1}
              onClick={() => { const p = currentPage - 1; setCurrentPage(p); fetchAll(p); }}
            >
              ← Previous
            </button>
            <span style={{ color: '#555', fontSize: '0.9rem' }}>Page {currentPage}</span>
            <button
              className="btn btn-secondary"
              disabled={!hasNextPunch && !hasNextTimeOff}
              onClick={() => { const p = currentPage + 1; setCurrentPage(p); fetchAll(p); }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function renderStatus(punch: TimePunch): React.ReactNode {
  if (punch.is_open) return <span className="status-badge status-active">Active</span>;
  if (punch.has_unresolved_flag) return <span className="status-badge" style={{ backgroundColor: '#f39c12', color: 'white' }}>Flagged</span>;
  if (punch.is_flagged && punch.flag_resolved) return <span className="status-badge status-paid">Resolved</span>;
  return <span className="status-badge status-draft">Complete</span>;
}

function typeStyle(type: string): React.CSSProperties {
  switch (type) {
    case 'sick': return { backgroundColor: '#e74c3c', color: 'white' };
    case 'vacation': return { backgroundColor: '#3498db', color: 'white' };
    case 'holiday': return { backgroundColor: '#27ae60', color: 'white' };
    default: return { backgroundColor: '#95a5a6', color: 'white' };
  }
}

export default AdminPunchDashboard;
